import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { isEqual, omit } from 'lodash'
import { type Blocker } from 'react-router-dom'
import { type ReactNode } from 'react'
import { AxiosError } from 'axios'
import { type ITabItem, toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { uiStore } from 'shared/store/uiStore'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import {
  type IParamsTestBroadcast,
  type IParamsBroadcast,
  type IResponseBroadcastById,
} from 'entities/Broadcast/api/types'
import { type Phone } from 'entities/Phone/model/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { checkAccessPage, EnumAccessPageKeys } from 'entities/ExportRouters/lib/checkAccessPage'
import { type IParamsSendFrom } from 'entities/SendFrom'
import { openRcsNotSupportedFilesModal } from 'entities/Rcs'
import { QuietHours } from 'entities/QuietHours/model'
import { QuietHoursClosureStore } from 'entities/QuietHours/store/quietHoursClosureStore'
import { TrackConversionStore } from 'widgets/TrackConversions/store/trackConversionStore'
import { RinglessMessageFieldStore } from 'widgets/MessageFormFields/RinglessMessageField'
import { SendFromStore } from 'widgets/SendFrom'
import { AiRepliesStore } from 'widgets/AiReplies'
import { BroadcastSMSStore } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastSMS/store/BroadcastSMSStore'
import { BroadcastContactsStore } from 'pages/broadcasts/ui/BroadcastView/ui/ContactsContent'
import { BroadcastAssigneeStore } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastAssignee/store/broadcastAssigneeStore'
import { BroadcastScheduleStore } from 'pages/broadcasts/ui/BroadcastView/ui/BrooadcastSchedule'
import { BroadcastReviewStore } from 'pages/broadcasts/ui/BroadcastView/ui/ReviewBroadcast/store/broadcastReviewStore'
import { BroadcastBadNumberStore } from 'pages/broadcasts/ui/BroadcastView/ui/BroadcastBadNumbers'
import {
  BroadcastCreateFieldEnum,
  EnumModeBroadcast,
  EnumTypeBroadcast,
  type IBroadcastCreateField,
} from 'pages/broadcasts/ui/BroadcastView/store/types'

export class BroadcastViewStore {
  get tabs(): ITabItem<EnumTypeBroadcast>[] {
    const tabs: ITabItem<EnumTypeBroadcast>[] = [
      {
        key: EnumTypeBroadcast.Sms,
        name: 'SMS',
        disabled: this.type !== EnumTypeBroadcast.Sms && this.isEditRecurringChild,
      },
      {
        key: EnumTypeBroadcast.Ringless,
        name: 'Ringless',
        disabled: this.type !== EnumTypeBroadcast.Ringless && this.isEditRecurringChild,
      },
    ]

    if (this._entities.inboxesStore.hasRcsInbox) {
      tabs.push({
        key: EnumTypeBroadcast.Rcs,
        name: 'RCS',
        disabled: this.type !== EnumTypeBroadcast.Rcs && this.isEditRecurringChild,
        statusProps: {
          intent: 'warning',
          emphasis: 'high',
          title: 'Beta',
        },
      })
    }

    return tabs
  }

  loading = true
  loadingCreate = false
  modalSaveDraft = nanoid()
  mode: EnumModeBroadcast = EnumModeBroadcast.Create
  name = ''

  private _isDragging = false
  private _numbersCheckType = 0
  private _enableQuietHours = false
  private _cacheParams: IParamsBroadcast | null = null
  private _type: EnumTypeBroadcast = EnumTypeBroadcast.Sms
  private _disposeBlockerModal: IReactionDisposer | null = null
  private _disposeTotalContacts: IReactionDisposer | null = null
  private _disposeBroadcastLimitPackageCount: IReactionDisposer | null = null
  private _disposeAttachmentsLimit: IReactionDisposer | null = null
  private _disposeFileSizeLimit: IReactionDisposer | null = null
  private _disposeNumbersCheckType: IReactionDisposer | null = null
  private _disposeSendFromNumber: IReactionDisposer | null = null
  private _disposeIsAircallReaction: IReactionDisposer | null = null
  private _disposeTypeReaction: IReactionDisposer | null = null
  private _disposeIsRCSReaction: IReactionDisposer | null = null
  broadcastSMSStore: BroadcastSMSStore
  ringlessMessageFieldStore: RinglessMessageFieldStore
  errorsMap: Map<IBroadcastCreateField, ReactNode> = new Map()

  broadcastContactsStore: BroadcastContactsStore
  broadcastAssigneeStore: BroadcastAssigneeStore
  broadcastScheduleStore: BroadcastScheduleStore
  broadcastReviewStore: BroadcastReviewStore
  trackConversionStore = new TrackConversionStore({
    clearError: () => {
      this.setError('conversion_id', '')
    },
  })
  aiRepliesStore: AiRepliesStore | null = null
  private readonly _broadcastBadNumberStore: BroadcastBadNumberStore
  private _isOpenAdvanced = false
  private _responseEditBroadcast: IResponseBroadcastById | null = null
  private _sendFromStore: SendFromStore
  private _showAiReplies = false

  constructor(
    private _entities: IEntitiesStore,
    response?: IResponseBroadcastById,
    isDuplicate?: boolean,
    openAdvanced?: boolean
  ) {
    makeAutoObservable(this)

    this._sendFromStore = new SendFromStore(this._entities, {
      excludeDeprioritizedNumbers: true,
      withDynamicSender: true,
    })
    this.broadcastSMSStore = new BroadcastSMSStore(
      {
        onFocus: () => {
          this.setError('smsMessage', '')
        },
        isMarketing: true,
      },
      this._entities
    )
    this.ringlessMessageFieldStore = new RinglessMessageFieldStore(this._entities, {
      clearParentError: () => {
        this.setError('ringlessMessage', '')
      },
    })
    this.broadcastContactsStore = new BroadcastContactsStore(this._entities)
    this.broadcastContactsStore = new BroadcastContactsStore(this._entities)
    this.broadcastAssigneeStore = new BroadcastAssigneeStore(this._entities, this._sendFromStore)
    this.broadcastScheduleStore = new BroadcastScheduleStore()
    this.broadcastReviewStore = new BroadcastReviewStore(this, this._entities)
    this._broadcastBadNumberStore = new BroadcastBadNumberStore(this._entities)
    this.init(response, isDuplicate, openAdvanced)
  }

  init = async (
    response?: IResponseBroadcastById,
    isDuplicate?: boolean,
    openAdvanced?: boolean
  ) => {
    this.loading = true

    await Promise.all([
      this._entities.integrationsStore.fetchIntegrations(),
      this._entities.mergeFieldStore.loadAllFields(),
    ])

    let promise: Promise<void> | null = null

    if (response) {
      runInAction(() => {
        if (isDuplicate) {
          this.mode = EnumModeBroadcast.Clone
          this.name = response.name + ' (copy)'
        } else {
          this._responseEditBroadcast = response
          this.mode = EnumModeBroadcast.Edit
          this.name = response.name
        }
      })

      const isRCS = response.send_from?.type === 'from_rcs_sender'

      if (isRCS) {
        this.setType(EnumTypeBroadcast.Rcs)
        this.broadcastSMSStore.messageFieldStore.setIsRCS(isRCS)
      }

      const assignee = response.assignee
      const numberId = response.number?.id || response.team?.number?.id
      const send_from = isRCS ? null : response.send_from

      const paramsSendFrom =
        send_from ||
        (numberId && {
          type: 'from_number',
          options: {
            number_id: numberId,
          },
        })

      if (paramsSendFrom) {
        this._sendFromStore.setSendFrom(paramsSendFrom).then(() => {
          this.broadcastAssigneeStore.reactionSendFromStoreInboxId()
        })
      } else {
        this.broadcastAssigneeStore.reactionSendFromStoreInboxId()
      }

      this.broadcastAssigneeStore.setAssignMemberId(
        assignee?.member_id || assignee?.type || 'default'
      )

      if (response.is_rvm) {
        this.setType(EnumTypeBroadcast.Ringless)
        const responseMediaShortUrl = response?.media_url?.[0]
        if (responseMediaShortUrl) this.ringlessMessageFieldStore.init(responseMediaShortUrl)
      } else {
        await this.broadcastSMSStore.init(response)
      }

      promise = this.broadcastContactsStore.init(response)

      this.broadcastScheduleStore.init({
        response,
        isEditRecurring: this.isEditRecurring,
        isEditRecurringChild: this.isEditRecurringChild,
      })

      if (response.is_track_conversion) {
        this._isOpenAdvanced = true
      }

      this.setNumbersCheckType(response.numbers_check_type ? 1 : 0)
      this._broadcastBadNumberStore.setChecked(Boolean(response.numbers_check_type))
      this._enableQuietHours = Boolean(response.enable_quiet_hours)

      this.trackConversionStore.init({
        is_track_conversion: response.is_track_conversion,
        conversion_id: response.conversion_id,
      })
    } else {
      runInAction(() => {
        this._enableQuietHours = this.isQuietHoursEnabled
      })
      this.broadcastAssigneeStore.reactionSendFromStoreInboxId()
    }

    await promise

    this._broadcastBadNumberStore.setTotal(this.broadcastContactsStore.total)

    runInAction(() => {
      if (!isDuplicate) {
        this._cacheParams = this.params
      }

      this.reactionBlockerModal()
      this.reactionTotalContacts()
      this.reactionBroadcastLimitPackageCount()
      this.reactionNumbersCheckType()
      this.reactionSendFromNumber()
      this.reactionIsAircallInbox()
      this.reactionIsRCS()

      this.broadcastScheduleStore.setSchedulingRecipientsMax(this.broadcast_limits_package_count)

      if (response?.package_count) {
        this.broadcastScheduleStore.setSchedulingRecipients(response.package_count)
      }

      const replyAutomationOptionsId = response?.reply_automation?.options.id

      if (openAdvanced || replyAutomationOptionsId) this._isOpenAdvanced = true

      if (
        this._entities.userPermissionsStore.getItem('can_manage_broadcasts') &&
        checkAccessPage(EnumAccessPageKeys.chatbot, this._entities)
      ) {
        this.aiRepliesStore = new AiRepliesStore({
          clearError: () => {
            this.setError('chatbot_id', '')
          },
        })

        this._showAiReplies = true

        if (replyAutomationOptionsId) {
          this.aiRepliesStore.isAiRepliesActive = true
          void this.aiRepliesStore.loadDataByChatbotId(replyAutomationOptionsId)
        }
      }
      this.reactionType()
      this.loading = false
    })
  }

  reset = () => {
    this._disposeBlockerModal?.()
    this._disposeAttachmentsLimit?.()
    this._disposeFileSizeLimit?.()
    this._disposeTotalContacts?.()
    this._disposeBroadcastLimitPackageCount?.()
    this._disposeNumbersCheckType?.()
    this._broadcastBadNumberStore.reset()
    this._disposeIsAircallReaction?.()
    this._disposeTypeReaction?.()
    this._disposeIsRCSReaction?.()
    this.broadcastAssigneeStore.dispose()

    uiStore.setRoutesBlocker(null)
  }

  reactionType = () => {
    this._disposeTypeReaction?.()
    this._disposeTypeReaction = reaction(
      () => this._type,
      (status) => {
        this._sendFromStore.setSkipUnverifiedNumbers(status !== EnumTypeBroadcast.Ringless)
        if (status === EnumTypeBroadcast.Rcs) {
          this.aiRepliesStore?.setDisabled(true, 'Handle replies with AI is not available for RCS')
          this.aiRepliesStore?.setAiRepliesState(false)
          this.broadcastSMSStore.messageFieldStore.validateRcsAttachments()
        } else {
          this.aiRepliesStore?.setDisabled(false, null)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionIsAircallInbox = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIsAircallReaction = reaction(
      () => this._sendFromStore.isAircall,
      (isAircall) => {
        this.broadcastSMSStore.messageFieldStore.setIsCurrentAirCall(isAircall)
      }
    )
  }

  reactionBlockerModal = () => {
    this._disposeBlockerModal?.()
    this._disposeBlockerModal = reaction(
      () => uiStore.routeBlocker,
      (blocker) => {
        if (blocker) {
          this.handleShowUnsavedModal(blocker)
        }
      }
    )
  }

  reactionTotalContacts = () => {
    this._disposeTotalContacts?.()
    this._disposeTotalContacts = reaction(
      () => [this.broadcastContactsStore.total, this.broadcast_limits_package_count],
      ([total, limit]) => {
        this.broadcastScheduleStore.setRequiredAdvancedSchedule(total > limit)
        this._broadcastBadNumberStore.setTotal(total)
      }
    )
  }

  reactionBroadcastLimitPackageCount = () => {
    this._disposeBroadcastLimitPackageCount?.()
    this._disposeBroadcastLimitPackageCount = reaction(
      () => this.broadcast_limits_package_count,
      this.broadcastScheduleStore.setSchedulingRecipientsMax,
      {
        fireImmediately: true,
      }
    )
  }

  reactionNumbersCheckType = () => {
    this._disposeNumbersCheckType?.()
    this._disposeNumbersCheckType = reaction(
      () => this._broadcastBadNumberStore.checked,
      (value) => {
        this.setNumbersCheckType(value ? 1 : 0)
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionSendFromNumber = () => {
    this._disposeSendFromNumber?.()
    this._disposeSendFromNumber = reaction(
      () => this.sendFrom,
      (value) => {
        const number_id = value?.options?.number_id || null
        this.broadcastContactsStore.setNumberId(number_id)
        if (number_id) {
          this.broadcastContactsStore.loadCount(number_id)
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  setDraggingCondition = (condition: boolean) => {
    this._isDragging = condition
  }

  setName = (name: string) => {
    this.name = name
  }

  private setType = (type: EnumTypeBroadcast) => {
    this._type = type
  }

  selectTypeTab = (tab: ITabItem<EnumTypeBroadcast>) => {
    const {
      messageFieldStore: { hasInvalidRcsAttachments },
    } = this.broadcastSMSStore

    if (tab.key === 'rcs' && hasInvalidRcsAttachments) {
      openRcsNotSupportedFilesModal({
        onContinue: () => this.setType(tab.key),
      })

      return
    }

    this.setType(tab.key)
  }

  setNumbersCheckType = (value: number) => {
    this._numbersCheckType = value
  }

  get quietHours() {
    return new QuietHours(this._entities.organizationStore.quietHoursSettings)
  }

  get isQuietHoursEnabled() {
    return this.quietHours.isQuietHoursEnabled
  }

  get isQuietHoursBlockOnWeekend() {
    return this.quietHours.isQuietHoursBlockOnWeekend
  }

  get quietHoursAllowedTimeFrom() {
    return this.quietHours.formatedQuietHours.allowedTimeFrom
  }

  get quietHoursAllowedTimeTo() {
    return this.quietHours.formatedQuietHours.allowedTimeTo
  }

  get quietHoursClosureStore() {
    return new QuietHoursClosureStore()
  }

  get enableQuietHours() {
    return this._enableQuietHours
  }

  setEnableQuietHours = (value: boolean) => {
    this._enableQuietHours = value
  }

  setOpenAdvanced = (value: boolean) => {
    this._isOpenAdvanced = value
  }

  handleShowUnsavedModal = (blocker: Blocker) => {
    if (blocker.state !== 'blocked') return

    const title = this.name
      ? `Save broadcast “${this.name}” before closing?`
      : 'Save broadcast before closing?'

    const isSaveChanges =
      this.mode === EnumModeBroadcast.Edit && this._responseEditBroadcast?.status !== 'draft'

    modalStore.addModal({
      id: this.modalSaveDraft,
      type: ModalTypeList.INFO,
      title: title,
      primaryAction: {
        text: isSaveChanges ? 'Save changes' : 'Save as draft',
        onAction: async () => {
          this._checkValidate(['name'])

          if (this.errorsMap.size) {
            blocker.reset()
            uiStore.setRoutesBlocker(null)
            modalStore.closeModal(this.modalSaveDraft)

            return
          }

          if (isSaveChanges) {
            await this.handleReviewBroadcast()
          } else {
            await this.handleCreateDraftBroadcast(() =>
              uiStore.changeRoute({
                path: blocker.location.pathname,
                type: uiStore.checkRouteType(blocker.location.pathname),
              })
            )
          }
          blocker.reset()
          uiStore.setRoutesBlocker(null)
          modalStore.closeModal(this.modalSaveDraft)
          this.broadcastReviewStore.close()
        },
      },
      additionalSecondaryAction: {
        text: 'Don’t save',
        onAction: () => {
          blocker.proceed()

          uiStore.setRoutesBlocker(null)
          uiStore.changeRoute({
            path: blocker.location.pathname,
            type: 'vue',
            method: 'replace',
          })
          modalStore.closeModal(this.modalSaveDraft)
          this.broadcastReviewStore.close()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          modalStore.closeModal(this.modalSaveDraft)
        },
      },
    })
  }

  private createBroadcast = async (params: IParamsBroadcast, onSuccess: () => void) => {
    const cacheParams = this._cacheParams

    try {
      this.loadingCreate = true

      this._cacheParams = this.params

      if (this.mode === EnumModeBroadcast.Edit) {
        await BroadcastApi.updateBroadcast(params)
      } else {
        await BroadcastApi.createBroadcast(params)
      }

      onSuccess()
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.errors.getError('error')) {
          toastStore.add({
            type: 'error',
            title: e.errors.getError('error'),
          })
        } else {
          const isBroadcastField = (key: string): key is IBroadcastCreateField =>
            key in BroadcastCreateFieldEnum
          e.errors.entries().map(([key, value]) => {
            if (isBroadcastField(key)) {
              this.setError(key, value.value)
            } else {
              toastStore.add({
                type: 'error',
                title: value.value,
              })
            }
          })
        }
      }

      this._cacheParams = cacheParams

      console.log(e)
    } finally {
      runInAction(() => {
        this.loadingCreate = false
      })
    }
  }

  handleCreateBroadcast = async (onSuccess: () => void) => {
    await this.createBroadcast(this.params, onSuccess)
  }

  handleCreateDraftBroadcast = (onSuccess: () => void) =>
    this.createBroadcast({ ...this.params, is_draft: true }, onSuccess)

  private _checkValidate = (exclude: string[] = []) => {
    if (!this.params.name.trim().length && !exclude.includes('name')) {
      this.setError('name', 'Please enter broadcast name')
    }

    if (this.params.is_rvm) {
      if (!this.params.media_url.length) {
        this.setError('ringlessMessage', 'Your message is the key – please select an audio')
      }
    } else {
      if (!this.params.message.trim().length && !this.params.media_url.length) {
        this.setError('smsMessage', 'Your message is the key – please write it above')
      }
    }

    if (!this.params.send_from) {
      this.setError('sendFrom', 'Please select the inbox to send from')
    }
    if (this.params.is_track_conversion && !this.params.conversion_id) {
      this.setError('conversion_id', 'Please select or create a conversion to continue')
    }

    if (this.aiRepliesStore?.isAiRepliesActive && !this.params.reply_automation?.options.id) {
      this.setError('chatbot_id', 'Please select an AI Agent')
    }

    if (!this.broadcastContactsStore.total && !this.broadcastContactsStore.showZeroState) {
      this.setError('contacts', [
        'Please select the contacts',
        <br key={'br'} />,
        'to receive the message',
      ])
    }
  }

  setError = (key: IBroadcastCreateField, error: ReactNode) => {
    if (key === 'conversion_id') {
      this.trackConversionStore.setError(error)
    }
    if (key === 'chatbot_id') {
      this.aiRepliesStore?.setError(error)
    }
    if (key === 'ringlessMessage') {
      this.ringlessMessageFieldStore.setError(error)
    }
    if (!!error) this.errorsMap.set(key, error)
    else this.errorsMap.delete(key)
  }

  clearErrors = () => {
    this.trackConversionStore.clearError()
    this.aiRepliesStore?.clearError()
    this.ringlessMessageFieldStore.clearError()
    this.errorsMap.clear()
  }

  handleReviewBroadcast = async () => {
    this.clearErrors()
    this._checkValidate()
    if (this.errorsMap.size) {
      return
    }
    if (this.type === 'sms' && this.broadcastSMSStore.isError) {
      return
    }

    await this.broadcastReviewStore.open()
  }

  get sendFromStore() {
    return this._sendFromStore
  }

  get isDraggingCondition() {
    return this._isDragging
  }

  get isImmediately() {
    return this.broadcastScheduleStore.scheduleStore.type === 'immediately'
  }

  get isEditRecurring() {
    return Boolean(
      this.mode === EnumModeBroadcast.Edit &&
        this._responseEditBroadcast?.recurring_template_settings
    )
  }

  get isEditRecurringChild() {
    return this.isEditRecurring && this._responseEditBroadcast?.status !== 'recurring_template'
  }

  get isExistChanges() {
    return !isEqual(omit(this._cacheParams, 'is_rvm'), omit(this.params, 'is_rvm'))
  }

  get number(): Phone | undefined | null {
    if (!this.sendFrom?.options) return

    if (this.sendFrom.options.number_id) {
      return this._entities.numbersStore.getItem(this.sendFrom?.options.number_id)
    }

    if (this.sendFrom.options.team_id) {
      const inbox = this._entities.inboxesStore.getItem(this.sendFrom.options.team_id)

      if (inbox && inbox.type === 'inbox') {
        const numberId = inbox?.sendingOptions?.find(
          (item) => item.key === this.sendFrom?.options?.smart_option
        )?.number_ids[0]

        return this._entities.numbersStore.getItem(numberId)
      }
    }
  }

  get testSMSParams() {
    const params: IParamsTestBroadcast = {
      media_url: this.broadcastSMSStore.messageDataParams.media_url,
      message: this.broadcastSMSStore.messageDataParams.message,
    }

    if (this.number?.id) params.number_id = this.number?.id
    if (this.type === 'rcs' && this.rcsInbox?.id) params.team_id = this.rcsInbox?.id

    return params
  }

  get broadcast_limits_package_count() {
    if (this.number?.isMobile) {
      return this._entities.organizationStore.broadcast_limits?.broadcasts_mobile || 0
    }
    if (this.number?.is_toll_free) {
      return this._entities.organizationStore.broadcast_limits?.broadcasts_toll_free || 0
    }
    if (this.number?.isShortCode) {
      return this._entities.organizationStore.broadcast_limits?.broadcasts_short_code || 0
    }
    return this._entities.organizationStore.broadcast_limits?.broadcasts_long_code || 0
  }

  get package_count() {
    const { scheduleStore, advanced_package_count } = this.broadcastScheduleStore

    if (
      !scheduleStore.isAdvancedSchedule &&
      this.broadcastContactsStore.total > this.broadcast_limits_package_count
    ) {
      return advanced_package_count || this.broadcast_limits_package_count
    }

    return scheduleStore.isAdvancedSchedule && advanced_package_count
      ? advanced_package_count
      : this.broadcast_limits_package_count
  }

  get typeParams() {
    if (this.type === 'sms' || this.type === 'rcs') {
      return this.broadcastSMSStore.messageDataParams
    }
    return this.ringlessMessageFieldStore.ringlessMessageDataParams
  }

  get rcsInbox() {
    return this._entities.inboxesStore.rcsInbox
  }

  get sendFrom(): IParamsSendFrom | null {
    if (this.type === 'rcs') {
      return {
        type: 'from_rcs_sender',
        options: {
          team_id: this.rcsInbox?.id,
          number_id: null,
        },
      }
    }

    return this._sendFromStore.paramsSendFrom
  }

  get params(): IParamsBroadcast {
    return {
      id:
        this.mode === EnumModeBroadcast.Edit && this._responseEditBroadcast
          ? this._responseEditBroadcast.id
          : null,
      send_from: this.sendFrom,
      assignee: this.broadcastAssigneeStore.assignParams,
      name: this.name,
      filters: this.broadcastContactsStore.paramsFilters,

      is_draft: false,

      isSharedShortCode: true,
      isShortCode: true,

      is_track_conversion: this.trackConversionStore.is_track_conversion,
      conversion_id: this.trackConversionStore.conversion_id,
      package_count: this.package_count,

      ...this.aiRepliesParams,

      is_cloned: false,
      is_rvm: false,
      short_domain: null,
      numbers_check_type: this._numbersCheckType,
      enable_quiet_hours: this._enableQuietHours,

      ...this.typeParams,
      ...this.broadcastScheduleStore.scheduleDataParams,
    }
  }

  get type() {
    return this._type
  }

  get badNumberStore() {
    return this._broadcastBadNumberStore
  }

  get isOpenAdvanced() {
    return this._isOpenAdvanced
  }

  get showAiReplies() {
    return this._showAiReplies
  }

  get aiRepliesParams() {
    const params: Partial<Pick<IParamsBroadcast, 'reply_automation'>> = {}
    if (this.aiRepliesStore?.chatbot_id) {
      params.reply_automation = {
        type: 'chatbot',
        options: {
          id: this.aiRepliesStore.chatbot_id,
        },
      }
    }

    return params
  }

  reactionIsRCS = () => {
    this._disposeIsRCSReaction?.()
    this._disposeIsRCSReaction = reaction(
      () => this.type,
      (type) => {
        this.broadcastSMSStore.messageFieldStore.setIsRCS(type === 'rcs')
      }
    )
  }
}
